import cn from 'clsx';
import { FC, useEffect, useRef, useState } from 'react';

import Icon from '../Icon';

import s from './Tooltip.module.scss';

export interface TooltipProps {
  isCancelButtonVisible?: boolean;
  direction?: 'left' | 'right' | 'center';
  isTooltipAbove?: boolean;
  content?: string | React.ReactNode;
  className?: string;
  tooltipClassName?: string;
  tooltipContainerClassName?: string;
  pointerClassName?: string;
  isDefaultTooltipActive?: boolean;
  isTooltipTriggerDisable?: boolean;
}

const Tooltip: FC<React.PropsWithChildren<React.PropsWithChildren<TooltipProps>>> = ({
  children,
  direction = 'left',
  isTooltipAbove,
  isDefaultTooltipActive,
  content,
  className,
  tooltipClassName,
  tooltipContainerClassName,
  pointerClassName,
  isCancelButtonVisible,
  isTooltipTriggerDisable = false
}) => {
  const [isTooltipActive, setIsTooltipActive] = useState(false);

  const triggerButtonWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsTooltipActive(Boolean(isDefaultTooltipActive));
  }, [isDefaultTooltipActive]);

  useEffect(() => {
    const handleOutsideClose = (e: any) => {
      if (isTooltipActive && !triggerButtonWrapperRef?.current?.contains(e?.target) && !isCancelButtonVisible) setIsTooltipActive(false);
    };
    document.addEventListener('click', handleOutsideClose);

    return () => document.removeEventListener('click', handleOutsideClose);
  }, [isTooltipActive, isCancelButtonVisible]);

  return (
    <div className={cn('relative', 'inline-block', className)}>
      <div
        className={cn(s['trigger-button-wrapper'], {
          [s['is-tooltip-above'] as string]: isTooltipAbove
        })}
        ref={triggerButtonWrapperRef}
      >
        <div onClick={() => isTooltipTriggerDisable === false && setIsTooltipActive((prevState) => (isCancelButtonVisible ? true : !prevState))}>
          {children}
        </div>
        {isTooltipActive && (
          <div className={s['pointer-align-wrapper']}>
            <div
              className={cn(s.pointer, pointerClassName, {
                [s['is-tooltip-above'] as string]: isTooltipAbove
              })}
            />
          </div>
        )}
      </div>

      {isTooltipActive && (
        <div
          className={cn(s['tooltip-container'], s[direction], tooltipContainerClassName, {
            [s['is-tooltip-above'] as string]: isTooltipAbove
          })}
        >
          <div
            className={cn(s['tooltip-wrapper'], {
              [s['is-tooltip-above'] as string]: isTooltipAbove
            })}
          >
            <span
              className={cn(
                s.tooltip,
                {
                  [s['is-cancel-button-true'] as string]: isCancelButtonVisible,
                  [s['is-cancel-button-false'] as string]: !isCancelButtonVisible
                },
                tooltipClassName
              )}
            >
              {content}
            </span>
            {isCancelButtonVisible && (
              <button type="button" className={s['cancel-button']} aria-label="cancel" onClick={() => setIsTooltipActive((prevState) => !prevState)}>
                <div className={s['cancel-button-icon-wrapper']}>
                  <Icon width="18" height="18" color="gray.50" type="close" />
                </div>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;
